import React, { useState, memo, useCallback } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import colors from '../../../../styling/colors';
import SubcategoryListTheme from '../../../../styling/themes/SubcategoryListTheme';
import compareProps from '../../../../utils/compareProps';
import HandOffLink from '../../../../utils/urls/HandOffLink';
import { pluckStyleBlock } from '../../../../utils/selectors/theme';

// Themes set up values used in either the CategoryNav or the LeftNav
const ListContainer = styled.ul`
  display: flex;
  flex-direction: column;
  min-height: ${({ hasMinHeight }) => (hasMinHeight ? '160px' : 'auto')};
  line-height: 1rem;
  list-style: none;
  margin-bottom: 24px;
  padding: 0;
  ${({ theme }) =>
    pluckStyleBlock(theme, SubcategoryListTheme, 'ListContainer')}

  & li:nth-child(n+${({ numToDisplay }) => 2 + numToDisplay}) {
    display: none;
  }

  & li:last-child {
    display: list-item;
  }
`;

const SubcategoryLinkHeaderContainer = styled.li`
  ${({ theme }) =>
    pluckStyleBlock(theme, SubcategoryListTheme, 'SubcategoryHeaderLink')};
`;

const SubcategoryLinkContainer = styled.li`
  ${({ theme }) =>
    pluckStyleBlock(theme, SubcategoryListTheme, 'SubcategoryLinkContainer')};
`;

const SubcategoryLink = styled(HandOffLink)`
  cursor: pointer;
  text-decoration: none;

  &,
  &:link,
  &:visited {
    color: ${colors('lapel')};
  }

  &:hover {
    color: ${colors('dustyGray')};
  }
`;

const SubcategoryHeaderLink = styled(SubcategoryLink)`
  font-weight: 700;
`;

const MoreOption = styled.li`
  width: fit-content;
  color: ${colors('gatsby')};
  border-bottom: 1px solid ${colors('gatsby')};
  cursor: pointer;
`;

const SubCategory = (subcatData, onNodeClick) => {
  const { title, href } = subcatData;

  return (
    <SubcategoryLinkContainer key={href}>
      <SubcategoryLink
        href={href}
        preRedirects={[() => onNodeClick(subcatData)]}
      >
        {title}
      </SubcategoryLink>
    </SubcategoryLinkContainer>
  );
};

const SubcategoryList = memo(
  ({
    className = '',
    maxDisplayNumber,
    subcategoryData,
    onNodeClick,
    hasMinHeight,
  }) => {
    const [hasHiddenSubcats, setHasHiddenSubcats] = useState(
      subcategoryData.childNodes.length > maxDisplayNumber,
    );
    const { title, childNodes, href } = subcategoryData;

    // Don't hide the extra items if its just going to replaced by the 'more' link
    const numToDisplay = hasHiddenSubcats
      ? maxDisplayNumber - 1
      : subcategoryData.childNodes.length;
    let moreButton = null;

    // 'nav-more' class is used in the handler in categoryNav/index.js fpr mouseout to prevent
    // the nav from accidently disappearing.
    if (hasHiddenSubcats) {
      moreButton = (
        <MoreOption
          className="nav-more"
          key="more"
          onClick={() => setHasHiddenSubcats(false)}
        >
          more
        </MoreOption>
      );
    }

    return (
      <ListContainer
        className={className}
        hasMinHeight={hasMinHeight}
        numToDisplay={numToDisplay}
      >
        <SubcategoryLinkHeaderContainer>
          <SubcategoryHeaderLink
            href={href}
            preRedirects={[() => onNodeClick(subcategoryData)]}
          >
            {title}
          </SubcategoryHeaderLink>
        </SubcategoryLinkHeaderContainer>
        {subcategoryData.childNodes.map(subcatData =>
          SubCategory(subcatData, onNodeClick),
        )}
        {moreButton}
      </ListContainer>
    );
  },
  (prevProps, nextProps) =>
    compareProps(prevProps, nextProps, [
      'subcategoryData',
      'maxDisplayNumber',
      'hasMinHeight',
    ]),
);

SubcategoryList.propTypes = {
  subcategoryData: PropTypes.shape({
    level: PropTypes.number,
    slug: PropTypes.string,
    href: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    type: PropTypes.string,
    childNodes: PropTypes.array.isRequired,
  }).isRequired,
  maxDisplayNumber: PropTypes.number,
  onNodeClick: PropTypes.func.isRequired,
};

SubcategoryList.defaultProps = {
  nodes: [],
};

export default SubcategoryList;
